import { createBoundaryParser } from '@/lib/validation/schema-loader'
import { formatFixedDecimal, formatNpsScore } from '@/features/studies/report/page/components/tabs/formatAbsoluteScore'
import {
  buildDeckPagerEntries,
  deckPagerIndexOf,
  readerVisibleSlides,
} from '@/features/studies/report/page/components/deck/deckPager'
import { prepareDeckForRender } from '@/features/studies/deck/render/prepareDeckForRender'
import publicStorylineSchema from '../../../../../../packages/shared-types/schemas/reporting/public-storyline.schema.json'

import type {
  MetricResult,
  PublicStoryline,
  StorylineEvidenceEntry,
  StorylineEvidenceUnit,
} from '@digital-twins/shared-types/reporting'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import type { FailedExhibit, StorylineDeckLinks } from '@/features/studies/report/reportViewState'

/** A cut result as readers receive it (`PublicCutResult`: no test name or p-value). */
export type CutResult = NonNullable<PublicStoryline['cutResults']>[number]
export type CutGroupResult = NonNullable<CutResult['groups']>[number]

/**
 * The storyline parser, built from the generated reader contract
 * (`PublicStoryline`: kept and moved findings only, no trace or flag). A
 * payload of the wrong shape resolves `{ ok: false }`, logged once, and the
 * analyst tab shows its typed unavailable state; it never throws.
 */
export const parseStoryline = createBoundaryParser<PublicStoryline>('report/storyline', publicStorylineSchema)

/**
 * The computed cuts a list of request ids names, in that order. Only
 * readable (`ok`) cuts carrying their unit and decimals are returned; an
 * unavailable cut has nothing to show.
 */
export function readableCuts(storyline: PublicStoryline, requestIds: ReadonlyArray<string>): Array<CutResult> {
  const byId = new Map((storyline.cutResults ?? []).map((result) => [result.requestId, result]))
  const cuts: Array<CutResult> = []
  const seen = new Set<string>()
  for (const requestId of requestIds) {
    const result = byId.get(requestId)
    if (!result || seen.has(requestId) || !isReadableCut(result)) {
      continue
    }
    seen.add(requestId)
    cuts.push(result)
  }
  return cuts
}

function isReadableCut(result: CutResult): boolean {
  return (
    result.status === 'ok' &&
    result.valueUnit != null &&
    typeof result.displayDecimals === 'number' &&
    (result.groups?.length ?? 0) > 0
  )
}

/**
 * One group's figure at the cut's own display decimals, which every group
 * of the cut shares (one grid, one decimals scope, as the L-E rule), rounded
 * half away from zero like the producer. Null when the group is counts only.
 */
export function formatCutGroupValue(result: CutResult, group: CutGroupResult): string | null {
  if (typeof group.value !== 'number' || result.valueUnit == null || result.displayDecimals == null) {
    return null
  }
  const text = formatFixedDecimal(group.value, result.displayDecimals)
  switch (result.valueUnit) {
    case 'percent':
      return `${text}%`
    case 'index':
      // An NPS score (the one `index` reading): always signed, "+25", "−5", "0".
      return formatNpsScore(group.value, result.displayDecimals)
    case 'scale_points':
      return text
    default: {
      const unhandled: never = result.valueUnit
      return unhandled
    }
  }
}

/** One source row: what a cited figure is about and how many twins it rests on. */
export interface StorylineSourceRow {
  key: string
  label: string
  /** Twins the figure rests on; null when the index records none. */
  base: number | null
  /** The stored display figure ("62%"); null when there is none to show. */
  value?: string | null
  /** A coded theme's count unit; null for any other figure. */
  unit?: StorylineEvidenceUnit | null
  /** Exact group definition and measured scale for a table cell. */
  tableCellContext?: StorylineEvidenceEntry['tableCellContext']
}

const CUT_EVIDENCE_PREFIX = 'cut:'

/** The verdict frames the thesis; it is never a source row nor a plotted figure. */
export const STUDY_VERDICT_EVIDENCE_ID = 'study_verdict'

/** Whether an evidence id names a cut (`cut:<requestId>[:<groupId>]`). */
export function isCutEvidenceId(evidenceId: string): boolean {
  return evidenceId.startsWith(CUT_EVIDENCE_PREFIX)
}

/** The storyline's evidence index by id; null for storylines accepted before it. */
export function evidenceIndexById(storyline: PublicStoryline): ReadonlyMap<string, StorylineEvidenceEntry> | null {
  const index = storyline.evidenceIndex
  if (!Array.isArray(index)) {
    return null
  }
  return new Map(index.map((entry) => [entry.id, entry]))
}

/**
 * The sources an evidence list cites, looked up, never re-derived: `cut:<id>`
 * reads every group of that cut, `cut:<id>:<group>` one group (from
 * `cutResults`); any other id reads its evidence index entry (label, stored
 * value, base, a theme's unit). A storyline without an index, or an id the
 * index lacks, falls back to the metric of that id. `study_verdict` is never
 * a source. An id that resolves to nothing is dropped (the storyline
 * validators resolve every id at write time). Each row once.
 */
export function storylineSources(
  storyline: PublicStoryline,
  evidenceIds: ReadonlyArray<string>,
  metricResults: Readonly<Record<string, MetricResult>> | null | undefined,
): Array<StorylineSourceRow> {
  const cuts = new Map((storyline.cutResults ?? []).map((result) => [result.requestId, result]))
  const index = evidenceIndexById(storyline)
  const rows: Array<StorylineSourceRow> = []
  const seen = new Set<string>()

  function push(row: StorylineSourceRow) {
    if (seen.has(row.key)) {
      return
    }
    seen.add(row.key)
    rows.push(row)
  }

  for (const evidenceId of evidenceIds) {
    if (evidenceId === STUDY_VERDICT_EVIDENCE_ID) {
      continue
    }
    if (isCutEvidenceId(evidenceId)) {
      const [requestId, groupId] = evidenceId.slice(CUT_EVIDENCE_PREFIX.length).split(':')
      const result = cuts.get(requestId)
      if (!result || result.status !== 'ok') {
        continue
      }
      for (const group of result.groups ?? []) {
        if (groupId === undefined || group.groupId === groupId) {
          push({
            key: `cut:${requestId}:${group.groupId}`,
            label: group.label,
            base: group.base,
            value: null,
            unit: null,
          })
        }
      }
      continue
    }
    const entry = index?.get(evidenceId)
    if (entry) {
      push({
        key: evidenceId,
        label: entry.label,
        base: typeof entry.base === 'number' ? entry.base : null,
        // Selected supporting quotes count no one: no figure to show.
        value: entry.unit === 'quotes' ? null : (entry.value ?? null),
        unit: entry.unit ?? null,
        ...(entry.tableCellContext ? { tableCellContext: entry.tableCellContext } : {}),
      })
      continue
    }
    const metric = metricResults?.[evidenceId]
    if (metric) {
      push({
        key: evidenceId,
        label: metric.display.label,
        base: metric.baseSize,
        value: null,
        unit: null,
      })
    }
  }
  return rows
}

/**
 * The slide a finding's exhibit points at: its first exhibit's slide, if any.
 * Every exhibit names a slideId, a cut-only slide included, so there is no
 * per-source case.
 */
export function findingSlideId(storyline: PublicStoryline, findingId: string): string | null {
  const exhibit = (storyline.exhibits ?? []).find((candidate) => candidate.findingIds.includes(findingId))
  return exhibit?.slideId ?? null
}

/**
 * The pager number (visible index + 1) of each slide a storyline exhibit
 * points at, as the viewer's deck pager counts: the deck as rendered, less
 * the slides the owner hid, with the failed exhibits the viewer is shown in
 * their sequence place. A slide the pager does not show gets no number.
 */
export function storylineSlideNumbersFor(
  storyline: PublicStoryline,
  deck: Deck,
  pager: { failedExhibits: Array<FailedExhibit>; slideOrder: Array<string> },
): ReadonlyMap<string, number> {
  const prepared = prepareDeckForRender(deck)
  const entries = buildDeckPagerEntries(
    readerVisibleSlides(prepared.slides, prepared.metadata?.hiddenSlideIndices),
    pager.failedExhibits,
    pager.slideOrder,
  )
  const numbers = new Map<string, number>()
  for (const exhibit of storyline.exhibits ?? []) {
    if (numbers.has(exhibit.slideId)) {
      continue
    }
    const index = deckPagerIndexOf(entries, exhibit.slideId)
    if (index >= 0) {
      numbers.set(exhibit.slideId, index + 1)
    }
  }
  return numbers
}

/** How a finding's slide link renders. */
export type StorylineExhibitLinkState =
  | { kind: 'live'; slideId: string; number: number }
  | { kind: 'pending' }
  | { kind: 'hidden' }

/**
 * A slide link from where the deck stands and the pager numbers the viewer
 * sees (visible index + 1): live when the slide is in the pager, pending
 * while the deck is on its way, else no link (deck failed or skipped, the
 * slide hidden by the owner, or never built).
 */
export function exhibitLinkState(
  slideId: string | null,
  deckLinks: StorylineDeckLinks,
  slideNumbers: ReadonlyMap<string, number>,
): StorylineExhibitLinkState {
  if (slideId === null) {
    return { kind: 'hidden' }
  }
  switch (deckLinks) {
    case 'pending':
      return { kind: 'pending' }
    case 'live': {
      const number = slideNumbers.get(slideId)
      return number === undefined ? { kind: 'hidden' } : { kind: 'live', slideId, number }
    }
    case 'none':
      return { kind: 'hidden' }
    default: {
      const unhandled: never = deckLinks
      return unhandled
    }
  }
}

/** Whether the storyline changed after the owner's thread started on an older one. */
export function storylineWasUpdated(
  threadRevision: string | null | undefined,
  currentRevision: string | null | undefined,
): boolean {
  return Boolean(threadRevision) && Boolean(currentRevision) && threadRevision !== currentRevision
}
